/**
 * inboxReplyCase preset — voice input for the Automatic Inbox Reply
 * ("Cases") setup in AgentsSettings.js.
 *
 * Each "case" is a matcher-rule that fires when incoming email matches its
 * trigger description, then either drafts a reply, stays silent, or only
 * tags the mail. Configuring one from scratch means filling ~10 fields the
 * user usually can't reason about in isolation — so we let them just talk
 * about the situation, and the AI extracts everything.
 *
 * Schema type: 'fields'
 *   Extracts the exact keys AgentsSettings.buildPayload consumes, so the
 *   caller drops them straight into a draft passed to <AgentEditor />:
 *     - name              (string, required) — 2-4 word case name
 *     - case              (string, required) — trigger description (WHEN clause)
 *     - type              (enum) Reply | No Reply | Only Tag
 *     - task              (string) — reply instructions (WHAT to say). Empty
 *                                    when type is not "Reply".
 *     - happinessMin      (number 1-10) — lower bound of matched mood range
 *     - happinessMax      (number 1-10) — upper bound (auto-inferred from
 *                                          case sentiment: positive → 6-10,
 *                                          negative → 1-3, neutral/all → 1-10)
 *     - dos               (string_array) — absolute "always" rules
 *     - donts             (string_array) — absolute "never" rules
 *     - useTag            (boolean) — auto-true if a tag name is extracted
 *     - tag               (string)  — short lowercase tag word/phrase
 *     - tagDescription    (string)  — one-line meaning of the tag
 *
 * Skipped on purpose (too fiddly for voice — user tweaks in the editor):
 *   - replyMode/replyTemplate — dedicated flow, needs pasted text
 *   - adherence               — irrelevant unless replyMode='template'
 *   - useCalendly / selectedEventType / calendlyGuidelines — needs UI pick
 *   - tagColor / tagIcon      — visual choices, we pick a sensible default
 *   - selectedTopic           — separate selector for reply-template
 *
 * The 6 guided questions cover the 80/20:
 *   1. When should this case fire? (→ case)
 *   2. What should happen — reply / silent / tag-only? (→ type)
 *   3. What should the reply say? (→ task)
 *   4. Any hard "always" rules? (→ dos)
 *   5. Any hard "never" rules? (→ donts)
 *   6. Should it add an inbox tag? Name + meaning? (→ useTag, tag, tagDescription)
 */

import React, { useEffect, useMemo, useState } from 'react';

const TYPE_OPTIONS = [
  { value: 'Reply',    label: 'Reply: draft an answer for review' },
  { value: 'No Reply', label: 'No Reply: leave the email alone (optionally tag it)' },
  { value: 'Only Tag', label: 'Only Tag: never reply, just add a colored label' },
];

// Auto-picked tag colors so the user doesn't have to think about hex codes
// mid-flow. Mapped by case sentiment so the palette reads intuitively in the
// inbox (green=good, amber=neutral question, blue=info, rose=bad).
const TAG_COLOR_BY_SENTIMENT = {
  positive: '#22c55e', // green
  neutral:  '#3b82f6', // blue
  question: '#f59e0b', // amber
  negative: '#dc2626', // rose
  silent:   '#6b7280', // gray
};

/**
 * Deduces which tag color best fits the extracted case based on happiness
 * range + action type. This is a UX affordance only — the user can still
 * change the color in the editor's tag picker afterwards.
 */
function pickTagColor({ happinessMin, happinessMax, type, task }) {
  if (type === 'No Reply' || type === 'Only Tag') {
    if (happinessMax <= 3) return TAG_COLOR_BY_SENTIMENT.negative;
    return TAG_COLOR_BY_SENTIMENT.silent;
  }
  if (happinessMin >= 6) return TAG_COLOR_BY_SENTIMENT.positive;
  if (happinessMax <= 3) return TAG_COLOR_BY_SENTIMENT.negative;
  // A "questions about pricing / info" case usually asks for something ---
  // detect by task wording.
  if (task && /price|cost|budget|question|info|detail/i.test(task)) {
    return TAG_COLOR_BY_SENTIMENT.question;
  }
  return TAG_COLOR_BY_SENTIMENT.neutral;
}

/**
 * Editable preview. The 4 primary fields (name, when, action, reply) are
 * front-and-centre; rules and tag config sit in a compact secondary block.
 * Mood range is a compact dual-slider represented as a min/max input pair.
 */
function InboxReplyCasePreview({ structured, onConfirm, onCancel, saving }) {
  const initial = structured?.fields || {};
  const [values, setValues] = useState({
    name:           initial.name           || '',
    case:           initial.case           || '',
    type:           initial.type           || 'Reply',
    task:           initial.task           || '',
    happinessMin:   Number(initial.happinessMin) || 1,
    happinessMax:   Number(initial.happinessMax) || 10,
    dos:            Array.isArray(initial.dos)   ? initial.dos   : [],
    donts:          Array.isArray(initial.donts) ? initial.donts : [],
    useTag:         !!initial.useTag || !!(initial.tag && initial.tag.trim()),
    tag:            initial.tag            || '',
    tagDescription: initial.tagDescription || '',
  });

  // Re-seed when the AI hands us a completely fresh structured result while
  // the modal is still mounted (rare, but happens if the user hits "Back to
  // transcript" and re-organises with a longer transcript).
  useEffect(() => {
    if (initial) {
      setValues({
        name:           initial.name           || '',
        case:           initial.case           || '',
        type:           initial.type           || 'Reply',
        task:           initial.task           || '',
        happinessMin:   Number(initial.happinessMin) || 1,
        happinessMax:   Number(initial.happinessMax) || 10,
        dos:            Array.isArray(initial.dos)   ? initial.dos   : [],
        donts:          Array.isArray(initial.donts) ? initial.donts : [],
        useTag:         !!initial.useTag || !!(initial.tag && initial.tag.trim()),
        tag:            initial.tag            || '',
        tagDescription: initial.tagDescription || '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structured?.fields?.case]);

  const update = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  // Guard against inverted / out-of-range moods
  const clampMood = (n) => Math.max(1, Math.min(10, Math.round(Number(n) || 0)));
  const setMoodMin = (v) => {
    const n = clampMood(v);
    setValues((p) => ({ ...p, happinessMin: n, happinessMax: Math.max(n, p.happinessMax) }));
  };
  const setMoodMax = (v) => {
    const n = clampMood(v);
    setValues((p) => ({ ...p, happinessMax: n, happinessMin: Math.min(n, p.happinessMin) }));
  };

  const isReply = values.type === 'Reply';
  const isSilent = values.type === 'No Reply' || values.type === 'Only Tag';

  // Editable rule chips — user can remove any misheard rule and add fresh ones.
  const [newDo, setNewDo] = useState('');
  const [newDont, setNewDont] = useState('');
  const addRule = (which) => {
    const raw = which === 'dos' ? newDo : newDont;
    const value = raw.trim();
    if (!value) return;
    setValues((p) => ({ ...p, [which]: [...p[which], value] }));
    if (which === 'dos') setNewDo(''); else setNewDont('');
  };
  const removeRule = (which, idx) => {
    setValues((p) => ({ ...p, [which]: p[which].filter((_, i) => i !== idx) }));
  };

  const canConfirm = useMemo(
    () => values.name.trim().length > 0 && values.case.trim().length >= 20,
    [values.name, values.case]
  );

  const moodLabel = useMemo(() => {
    const lo = values.happinessMin;
    const hi = values.happinessMax;
    if (lo <= 1 && hi >= 10) return 'Any reply mood';
    if (lo >= 6) return 'Positive replies only';
    if (hi <= 3) return 'Negative replies only';
    return `Rating ${lo}–${hi}`;
  }, [values.happinessMin, values.happinessMax]);

  return (
    <div className="speakEngine__body speakEngine__body--preview">
      <div className="speakEngine__previewHead">
        <div>
          <h3>Case ready to review</h3>
          {structured?.summary && <p className="speakEngine__previewSummary">{structured.summary}</p>}
        </div>
      </div>

      <div className="speakEngine__fieldsGrid">
        <div className="speakEngine__fieldsRow">
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Case name</span>
            <input
              type="text"
              className="speakEngine__fieldInput"
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g. Interested"
            />
          </label>
          <label className="speakEngine__field">
            <span className="speakEngine__fieldLabel">Action</span>
            <select
              className="speakEngine__fieldInput"
              value={values.type}
              onChange={(e) => update('type', e.target.value)}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="speakEngine__field speakEngine__field--wide">
          <span className="speakEngine__fieldLabel">
            When it should fire
            <span className="speakEngine__fieldCounter">
              {values.case.length} chars {values.case.length < 20 && '· needs more detail'}
            </span>
          </span>
          <textarea
            className="speakEngine__fieldTextarea"
            rows={3}
            value={values.case}
            onChange={(e) => update('case', e.target.value)}
            placeholder="Describe the incoming email the matcher should trigger on. Example: The sender replied positively and wants to know more about what we offer."
          />
        </label>

        {isReply && (
          <label className="speakEngine__field speakEngine__field--wide">
            <span className="speakEngine__fieldLabel">Reply instructions</span>
            <textarea
              className="speakEngine__fieldTextarea"
              rows={4}
              value={values.task}
              onChange={(e) => update('task', e.target.value)}
              placeholder="What should the AI-drafted reply say and how? Tone, structure, key points to cover."
            />
          </label>
        )}

        {/* Mood range — compact dual-input plus one plain-language label. */}
        <div className="speakEngine__field speakEngine__field--wide">
          <span className="speakEngine__fieldLabel">
            Reply mood range
            <span className="speakEngine__fieldCounter">{moodLabel}</span>
          </span>
          <div className="speakEngine__inboxMoodRow">
            <label className="speakEngine__inboxMoodBox">
              <span className="speakEngine__inboxMoodTag">from</span>
              <input
                type="number" min="1" max="10"
                className="speakEngine__fieldInput"
                value={values.happinessMin}
                onChange={(e) => setMoodMin(e.target.value)}
              />
            </label>
            <span className="speakEngine__inboxMoodDash" aria-hidden>–</span>
            <label className="speakEngine__inboxMoodBox">
              <span className="speakEngine__inboxMoodTag">to</span>
              <input
                type="number" min="1" max="10"
                className="speakEngine__fieldInput"
                value={values.happinessMax}
                onChange={(e) => setMoodMax(e.target.value)}
              />
            </label>
            <span className="speakEngine__inboxMoodHint">1 = angry · 5 = neutral · 10 = enthusiastic</span>
          </div>
        </div>

        {/* Always-do / never-do rule chips */}
        {isReply && (
          <div className="speakEngine__fieldsRow">
            <div className="speakEngine__field">
              <span className="speakEngine__fieldLabel">Always do…</span>
              <div className="speakEngine__chipList">
                {values.dos.map((rule, i) => (
                  <span key={i} className="speakEngine__chip speakEngine__chip--do">
                    {rule}
                    <button type="button" onClick={() => removeRule('dos', i)} aria-label={`Remove: ${rule}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="speakEngine__chipInputRow">
                <input
                  type="text"
                  className="speakEngine__fieldInput"
                  value={newDo}
                  onChange={(e) => setNewDo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRule('dos'); } }}
                  placeholder="Add another always-do rule"
                />
                <button type="button" className="speakEngine__btn speakEngine__btn--ghost speakEngine__btn--small" onClick={() => addRule('dos')} disabled={!newDo.trim()}>Add</button>
              </div>
            </div>
            <div className="speakEngine__field">
              <span className="speakEngine__fieldLabel">Never do…</span>
              <div className="speakEngine__chipList">
                {values.donts.map((rule, i) => (
                  <span key={i} className="speakEngine__chip speakEngine__chip--dont">
                    {rule}
                    <button type="button" onClick={() => removeRule('donts', i)} aria-label={`Remove: ${rule}`}>×</button>
                  </span>
                ))}
              </div>
              <div className="speakEngine__chipInputRow">
                <input
                  type="text"
                  className="speakEngine__fieldInput"
                  value={newDont}
                  onChange={(e) => setNewDont(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRule('donts'); } }}
                  placeholder="Add another never rule"
                />
                <button type="button" className="speakEngine__btn speakEngine__btn--ghost speakEngine__btn--small" onClick={() => addRule('donts')} disabled={!newDont.trim()}>Add</button>
              </div>
            </div>
          </div>
        )}

        {/* Inbox tag toggle + name/desc.
            Outer wrapper is intentionally a <div> (not <label>) — the tag
            name / meaning inputs need their own <label> children, and HTML
            forbids nested labels (a click on the inner inputs would also
            toggle the outer checkbox, which broke the earlier draft). */}
        <div className="speakEngine__field speakEngine__field--wide speakEngine__inboxTagBlock">
          <label className="speakEngine__inboxTagRow">
            <input
              type="checkbox"
              checked={values.useTag}
              onChange={(e) => update('useTag', e.target.checked)}
            />
            <span className="speakEngine__inboxTagTitle">Add a colored inbox tag</span>
            <span className="speakEngine__inboxTagSub">Colour is auto-picked from the case. Change it later in the editor.</span>
          </label>
          {values.useTag && (
            <div className="speakEngine__fieldsRow speakEngine__inboxTagFields">
              <label className="speakEngine__field">
                <span className="speakEngine__fieldLabel">Tag name</span>
                <input
                  type="text"
                  className="speakEngine__fieldInput"
                  value={values.tag}
                  onChange={(e) => update('tag', e.target.value)}
                  placeholder="e.g. interested"
                />
              </label>
              <label className="speakEngine__field">
                <span className="speakEngine__fieldLabel">What it means</span>
                <input
                  type="text"
                  className="speakEngine__fieldInput"
                  value={values.tagDescription}
                  onChange={(e) => update('tagDescription', e.target.value)}
                  placeholder="e.g. Positive reply, worth following up quickly."
                />
              </label>
            </div>
          )}
        </div>
      </div>

      {isSilent && values.task.trim() && (
        <div className="speakEngine__note">
          You picked <strong>{values.type}</strong>, so the reply text above is ignored.
          The AI still uses your case description to match this scenario correctly.
        </div>
      )}

      <div className="speakEngine__previewActions">
        <button type="button" className="speakEngine__btn speakEngine__btn--ghost" onClick={onCancel} disabled={saving}>
          Back to transcript
        </button>
        <button
          type="button"
          className="speakEngine__btn speakEngine__btn--primary"
          onClick={() => onConfirm({ ...values, tagColor: pickTagColor(values) })}
          disabled={saving || !canConfirm}
          title={!canConfirm ? 'Give the case a name and a trigger description of at least 20 characters.' : ''}
        >
          {saving
            ? <><span className="speakEngine__btnSpinner" aria-hidden /> Opening editor…</>
            : <>Open in case editor</>}
        </button>
      </div>
    </div>
  );
}

const inboxReplyCasePreset = {
  id: 'inboxReplyCase',
  title: 'Add an inbox-reply case',
  subtitle: 'AI drafts the trigger, action, rules and tag for you.',
  intro: {
    heading: 'Tap and describe the case',
    hint: 'Talk freely for 60–120 seconds. Loosely follow the six prompts. Anything you skip falls back to safe defaults.',
    questionsHeading: 'Guiding questions',
    questions: [
      'When should this case fire? Describe the incoming email: what does the sender say, what do they want?',
      'What should happen: draft a reply for you to review, stay silent (no answer), or only add a coloured inbox tag?',
      'If it should reply, what should the reply say? Tone, key points, any specific ask to close with.',
      'Any hard "always do this" rules? (e.g. always answer in the sender\'s language, always under five sentences)',
      'Any hard "never do this" rules? (e.g. never quote a fixed price, never promise a deadline)',
      'Should it also add a coloured inbox tag? Give it a short name and one line about what it means.',
    ],
  },
  organizeCta: 'Draft the case',
  organizingText: 'AI is turning your description into a matcher-ready case: trigger, action, mood range, always/never rules, and a coloured tag. Usually 3–8 seconds.',
  doneText: () => 'Case drafted. Review and save.',
  schema: {
    type: 'fields',
    fields: [
      {
        id: 'name',
        label: 'Case name',
        type: 'string',
        required: true,
        hint: 'Short 2-4 word name for the case. Human-friendly, no emojis. Examples: "Interested", "Pricing question", "Wrong contact", "Out of office".',
      },
      {
        id: 'case',
        label: 'When to fire (trigger description)',
        type: 'long_string',
        required: true,
        hint: 'One or two sentences describing WHAT the incoming email looks like when this case should match. Always start with "The sender…". Be concrete about the sender\'s intent so the matcher can distinguish this from other cases. Example: "The sender replied positively — they are interested, curious, or want to know more about what we offer." DO NOT include reply instructions here — those go in "task".',
      },
      {
        id: 'type',
        label: 'Action type',
        type: 'enum',
        options: ['Reply', 'No Reply', 'Only Tag'],
        hint: 'How the app should respond when this case matches. "Reply" = draft an answer for the user to review (default when the user talks about a reply). "No Reply" = mark the mail as handled without writing anything (use for clear no\'s, unsubscribes, hostile messages). "Only Tag" = just add a coloured inbox tag but never draft (use for out-of-office / auto-replies / system mail). When uncertain: Reply.',
      },
      {
        id: 'task',
        label: 'Reply instructions',
        type: 'long_string',
        hint: 'How the reply should sound and what it should cover, in the language the user spoke. Concrete guidance — tone, structure, key points, close (e.g. "propose a call", "ask for the right contact"). Leave "" if the user picked "No Reply" or "Only Tag". Write in polished prose, no bullet lists.',
      },
      {
        id: 'happinessMin',
        label: 'Reply mood, lower bound',
        type: 'number',
        hint: 'Lower bound (1-10) of the reply-mood range this case matches. The mail analyzer rates every reply 1 (angry) → 10 (enthusiastic). Rules of thumb:\n- Clearly POSITIVE cases (interested, wants demo, pricing question, wants more info) → 5 or 6\n- Clearly NEGATIVE cases (not interested, unsubscribe, complaint, hostile) → 1\n- Neutral / all-mood cases (wrong contact, out of office, generic questions) → 1\nWhen uncertain: 1.',
      },
      {
        id: 'happinessMax',
        label: 'Reply mood, upper bound',
        type: 'number',
        hint: 'Upper bound (1-10) of the reply-mood range. Rules of thumb:\n- POSITIVE cases → 10\n- NEGATIVE cases (unsubscribe, complaint, do-not-contact) → 3 or 4\n- Ambiguous "not right now / follow up later" → 8\n- Neutral cases (wrong contact, auto-reply, generic) → 10\nMust be >= happinessMin. When uncertain: 10.',
      },
      {
        id: 'dos',
        label: 'Always-do rules',
        type: 'string_array',
        hint: 'Absolute rules that must ALWAYS be respected for this case, one rule per array entry. Phrased as short imperative sentences starting with "Always ...". Return [] if the user did not give any. Examples: "Always answer in the sender\'s language.", "Always end with one clear question.", "Always stay under five sentences.".',
      },
      {
        id: 'donts',
        label: 'Never-do rules',
        type: 'string_array',
        hint: 'Absolute NEVER rules for this case, one per entry. Phrased as short imperatives starting with "Never ...". Return [] if the user did not give any. Examples: "Never quote a fixed price.", "Never promise a delivery date.", "Never invent facts or references.", "Never apologise for reaching out.".',
      },
      {
        id: 'useTag',
        label: 'Add coloured inbox tag?',
        type: 'boolean',
        hint: 'true if the user asked for an inbox tag / label / colour marker, OR if the case is a distinctive category worth marking in the inbox (interested / pricing / not interested / auto-reply etc.). false if the user explicitly said they don\'t want a tag.',
      },
      {
        id: 'tag',
        label: 'Tag name',
        type: 'string',
        hint: 'A single short lowercase tag name (1-3 words, no punctuation). Only set when useTag is true. Examples: "interested", "pricing", "wrong contact", "not interested", "auto-reply". Return "" when useTag is false.',
      },
      {
        id: 'tagDescription',
        label: 'Tag meaning',
        type: 'string',
        hint: 'One short sentence describing what the tag stands for in the inbox — helps the user recognise it at a glance. Only set when useTag is true. Examples: "Positive reply — worth following up quickly.", "Asked about cost or budget.", "Automated message, no human behind it.".',
      },
    ],
  },
  PreviewComponent: InboxReplyCasePreview,
};

export default inboxReplyCasePreset;
